import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Power, PowerOff, Trash2, Pencil } from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Staff() {
  const [rows, setRows] = useState([]);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/users"),
      api.get("/api-keys/summary").catch(() => ({ data: [] })),
    ]).then(([u, k]) => {
      setRows(u.data);
      setSystems(k.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const systemNames = (ids) => {
    if (!ids || ids.length === 0) return null;
    return ids
      .map((id) => systems.find((s) => s.id === id)?.name || id.slice(0, 8))
      .join(", ");
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(!u.active ? "Activated" : "Deactivated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.mobile})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="px-10 py-10">
      <header className="mb-8 flex items-end justify-between gap-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">Access Control</div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl" data-testid="staff-title">
            Staff
          </h1>
          <p className="mt-3 max-w-xl text-sm text-muted-foreground">
            Assign each staff member to one or more systems. They will only see conversations that belong to those systems. Leave empty to let them see everything.
          </p>
        </div>
        <AddStaffDialog systems={systems} onDone={load} />
      </header>

      <div className="border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Name</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Mobile</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Role</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Systems allowed</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Status</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Added</TableHead>
              <TableHead className="text-right text-[10px] font-bold uppercase tracking-[0.2em]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No staff yet</TableCell></TableRow>
            ) : (
              rows.map((u) => (
                <TableRow key={u.id} className="border-border" data-testid={`staff-row-${u.mobile}`}>
                  <TableCell className="font-display font-bold">{u.name}</TableCell>
                  <TableCell className="font-mono">+91 {u.mobile}</TableCell>
                  <TableCell>
                    <span className="border border-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                      {u.role}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[240px] truncate text-xs">
                    {u.role === "admin"
                      ? <span className="text-muted-foreground/70">all (admin)</span>
                      : systemNames(u.allowed_api_key_ids) || <span className="text-muted-foreground/70">all systems</span>}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${u.active ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}`}>
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${u.active ? "bg-emerald-500" : "bg-red-500"}`} />
                      {u.active ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {u.created_at?.slice(0, 10) || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setEditing(u)} data-testid={`edit-staff-${u.mobile}`} aria-label="Edit">
                        <Pencil className="h-4 w-4 text-muted-foreground" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => toggleActive(u)} data-testid={`toggle-staff-${u.mobile}`} aria-label="Toggle">
                        {u.active ? <PowerOff className="h-4 w-4 text-red-500" /> : <Power className="h-4 w-4 text-emerald-500" />}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`delete-staff-${u.mobile}`} aria-label="Delete">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <EditStaffDialog user={editing} systems={systems} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function SystemsPicker({ systems, value, onChange, testidPrefix }) {
  const toggle = (id) => {
    const set = new Set(value || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    onChange(Array.from(set));
  };
  if (systems.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground">
        No API keys created yet. Create keys under <span className="font-mono">API Keys</span> first, then come back here to assign them.
      </p>
    );
  }
  return (
    <div className="space-y-1.5" data-testid={`${testidPrefix}-systems-picker`}>
      {systems.map((s) => {
        const checked = (value || []).includes(s.id);
        return (
          <label key={s.id} className="flex items-center gap-2 border border-border px-2 py-1.5 text-xs" data-testid={`${testidPrefix}-system-${s.id}`}>
            <input type="checkbox" checked={checked} onChange={() => toggle(s.id)} />
            <span className="font-display font-bold">{s.name}</span>
            <span className="ml-auto font-mono text-[10px] text-muted-foreground">{s.id.slice(0, 8)}</span>
          </label>
        );
      })}
      <p className="pt-1 text-[10px] text-muted-foreground">
        Leave empty for &quot;all systems&quot; (backward-compatible for existing staff).
      </p>
    </div>
  );
}

function AddStaffDialog({ systems, onDone }) {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("staff");
  const [allowed, setAllowed] = useState([]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!/^\d{10}$/.test(mobile)) return toast.error("Enter valid 10-digit mobile");
    if (!name.trim()) return toast.error("Enter name");
    setSaving(true);
    try {
      await api.post("/users", {
        mobile, name, role,
        allowed_api_key_ids: role === "admin" ? [] : allowed,
      });
      toast.success("Staff added");
      setOpen(false);
      setMobile(""); setName(""); setRole("staff"); setAllowed([]);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm" data-testid="add-staff-btn">
          <UserPlus className="mr-2 h-4 w-4" /> Add staff
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display">Add staff member</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-sm" data-testid="staff-name-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Mobile (10-digit)</Label>
            <div className="mt-1 flex border border-input">
              <span className="border-r border-input bg-muted px-3 py-2 text-sm text-muted-foreground">+91</span>
              <Input value={mobile} maxLength={10} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} className="rounded-none border-0 focus-visible:ring-0" data-testid="staff-mobile-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="staff-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {role === "staff" && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em]">Systems this staff can view</Label>
              <div className="mt-2">
                <SystemsPicker systems={systems} value={allowed} onChange={setAllowed} testidPrefix="add-staff" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm" data-testid="confirm-add-staff-btn">
            {saving ? "Saving…" : "Add staff"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditStaffDialog({ user, systems, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [role, setRole] = useState("staff");
  const [allowed, setAllowed] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setMobile(String(user.mobile || "").replace(/^91/, ""));
      setRole(user.role || "staff");
      setAllowed(user.allowed_api_key_ids || []);
    }
  }, [user]);

  if (!user) return null;

  const submit = async () => {
    if (!/^\d{10}$/.test(mobile)) return toast.error("Enter valid 10-digit mobile");
    if (!name.trim()) return toast.error("Enter name");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        role,
        allowed_api_key_ids: role === "admin" ? [] : allowed,
      };
      const originalMobile = String(user.mobile || "").replace(/^91/, "");
      if (mobile !== originalMobile) payload.mobile = mobile;
      await api.patch(`/users/${user.id}`, payload);
      toast.success("Staff updated");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm" data-testid="edit-staff-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit staff</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-sm" data-testid="edit-staff-name-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Mobile (10-digit)</Label>
            <div className="mt-1 flex border border-input">
              <span className="border-r border-input bg-muted px-3 py-2 text-sm text-muted-foreground">+91</span>
              <Input
                value={mobile} maxLength={10}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                className="rounded-none border-0 focus-visible:ring-0"
                data-testid="edit-staff-mobile-input"
              />
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              This is the login number — updating it changes the OTP destination immediately.
            </p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-staff-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {role === "staff" && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em]">Systems this staff can view</Label>
              <div className="mt-2">
                <SystemsPicker systems={systems} value={allowed} onChange={setAllowed} testidPrefix="edit-staff" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm" data-testid="confirm-edit-staff-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
